import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient, SESSION_EXPIRED_EVENT } from '@/lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar?: string | null;
  phone?: string | null;
  twoFactorEnabled?: boolean;
  hasCompletedOnboarding?: boolean;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Set when 2FA is required during login — the userId to pass to verifyTwoFactor */
  pendingTwoFactorUserId: string | null;
  /** Counter that increments to trigger the onboarding tour to reopen manually */
  onboardingTourTrigger: number;
  /** True when the session was force-expired (refresh token rejected) — the login page shows a banner */
  sessionExpired: boolean;

  login: (email: string, password: string) => Promise<void>;
  verifyTwoFactor: (userId: string, code: string, rememberDevice?: boolean) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  fetchUser: () => Promise<void>;
  setUser: (user: User) => void;
  triggerOnboarding: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      pendingTwoFactorUserId: null,
      onboardingTourTrigger: 0,
      sessionExpired: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const res: any = await apiClient.post('/auth/login', { email, password });

          if (res.requiresTwoFactor) {
            set({ pendingTwoFactorUserId: res.userId, isLoading: false });
            return;
          }

          if (res.success && res.data) {
            set({
              token: res.data.token,
              refreshToken: res.data.refreshToken,
              user: res.data.user,
              isAuthenticated: true,
              isLoading: false,
              pendingTwoFactorUserId: null,
              sessionExpired: false,
            });
            return;
          }

          // Response was successful but had no data — still need to reset loading state
          set({ isLoading: false, pendingTwoFactorUserId: null });
        } catch (error) {
          set({ isLoading: false, pendingTwoFactorUserId: null });
          throw error;
        }
      },

      verifyTwoFactor: async (userId: string, code: string, rememberDevice?: boolean) => {
        set({ isLoading: true });
        try {
          const res = await apiClient.post<{
            token: string;
            refreshToken: string;
            user: User;
          }>('/auth/verify-2fa', { userId, code, rememberDevice });

          if (res.success && res.data) {
            set({
              token: res.data.token,
              refreshToken: res.data.refreshToken,
              user: res.data.user,
              isAuthenticated: true,
              isLoading: false,
              pendingTwoFactorUserId: null,
              sessionExpired: false,
            });
            return;
          }

          // Response was successful but had no data — still need to reset loading state
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (email: string, password: string, name: string) => {
        set({ isLoading: true });
        try {
          const res = await apiClient.post<{
            token: string;
            refreshToken: string;
            user: User;
          }>('/auth/register', { email, password, name });

          if (res.success && res.data) {
            set({
              token: res.data.token,
              refreshToken: res.data.refreshToken,
              user: res.data.user,
              isAuthenticated: true,
              isLoading: false,
              sessionExpired: false,
            });
            return;
          }

          // Response was successful but had no data — still need to reset loading state
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        // Best-effort server-side revocation BEFORE clearing local state. We
        // send the REFRESH token (not the access token) because it's still valid
        // when the idle timeout fires long after the access token has expired.
        // The server bumps tokenVersion, invalidating this session's refresh
        // token. Swallow failures — logout must succeed locally regardless.
        const refreshTokenValue = get().refreshToken;
        if (refreshTokenValue) {
          apiClient.post('/auth/logout', { refreshToken: refreshTokenValue }).catch(() => {
            // Swallow — logout must succeed locally even if the network fails.
          });
        }
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          pendingTwoFactorUserId: null,
        });
        // Remove the persisted auth entry entirely from localStorage, so a
        // fresh login (or role switch) in the same tab starts completely clean
        // with no stale token/user data from the previous session.
        useAuthStore.persist.clearStorage();
      },

      fetchUser: async () => {
        try {
          const res = await apiClient.get<User>('/auth/me');
          if (res.success && res.data) {
            set({ user: res.data, isAuthenticated: true });
          }
        } catch {
          get().logout();
        }
      },

      setUser: (user: User) => set({ user }),

      triggerOnboarding: () => {
        set({ onboardingTourTrigger: get().onboardingTourTrigger + 1 });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Listen for the session-expiry signal from the API client (dispatched when a
// refresh attempt fails — the refresh token is expired/revoked server-side).
// Force-logout so the user is returned to the login screen instead of being
// stuck on a dashboard where every request 401s with no recovery. Also covers
// multi-tab sessions: logging out (or idle-timeout) in one tab revokes the
// refresh token server-side, so other tabs detect it here and log out cleanly.
if (typeof window !== 'undefined') {
  window.addEventListener(SESSION_EXPIRED_EVENT, () => {
    useAuthStore.setState({ sessionExpired: true });
    useAuthStore.getState().logout();
  });
}
